import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import {
  draftSaveIndicator,
  type DraftSaveQueue,
  type DraftSaveSnapshot,
  type StoreWebsiteDocument,
  type SupabaseClient,
  type WebsiteEditRegion,
  type WebsiteFrameDevice,
  type WebsiteTemplateManifest,
} from '@dukanti/shared';
import { env } from '@dukanti/shared/env';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useRegionEditor, type RegionEditor } from '../hooks/use-region-editor';
import { useFailedSaveCloseGuard } from '../hooks/use-failed-save-close-guard';
import { useUnsavedEditGuard } from '../hooks/use-unsaved-edit-guard';
import { useWebsiteT } from '../hooks/use-website-t';
import { regionEditLabel } from '../lib/region-labels';
import { unfinishedDestinationOf, type UnfinishedDestination } from '../lib/slot-values';
import { DraftSaveStatusChip } from './draft-save-status';
import { EditorLeaveDialog } from './editor-leave-dialog';
import { RegionMediaForm } from './region-media-form';
import { RegionNewsletterForm } from './region-newsletter-form';
import { RegionReferenceForm } from './region-reference-form';
import { RegionTextForm } from './region-text-form';
import { UnsavedChangesDialog } from './unsaved-changes-dialog';
import { WebsiteRuntimeNotice } from './website-runtime-notice';

export interface RegionEditDialogProps {
  /**
   * The queue owns every save of this dialog; the destination editor's
   * pickers and readiness (epic #228 P5) read through this client.
   */
  readonly client: SupabaseClient;
  readonly storeId: string;
  /** Epic #228 P3: the template whose draft this dialog edits (the queue is bound to it). */
  readonly templateId: string;
  readonly manifest: WebsiteTemplateManifest;
  readonly document: StoreWebsiteDocument;
  readonly region: WebsiteEditRegion;
  /** Epic #228 P4: the template's auto-save queue and its live snapshot. */
  readonly queue: DraftSaveQueue;
  readonly snapshot: DraftSaveSnapshot;
  /** The dialog is over. The parent unmounts it. */
  readonly onClose: () => void;
  /**
   * Grows each time the canvas refused a navigation while this dialog was
   * open. With local-only unsaved work that asks the discard question;
   * otherwise the dialog is already the answer.
   */
  readonly attentionSignal?: number;
  /** The already-mounted canvas discovered an unsupported contract; keep this dialog and input. */
  readonly runtimeUnavailable?: boolean;
  readonly capturePoster?: (file: File) => Promise<File>;
  /**
   * Epic #228 P6: the canvas device toggle. A media crop opens on this
   * device's tab; the dialog never changes the canvas device.
   */
  readonly device?: WebsiteFrameDevice;
}

/**
 * The centered editing dialog (Screen 4) over a dimmed, softly blurred
 * storefront. One region per dialog. Epic #228 P4: there is no Save; every
 * valid edit is auto-saved by the template's queue, the footer shows the
 * save status and **Done** closes. X, Escape, a click outside and Done all
 * flush the queue and close; they ask first only when the dialog holds
 * something the queue does not (an invalid field, a file on its way).
 */
export function RegionEditDialog({
  client,
  storeId,
  templateId,
  manifest,
  document,
  region,
  queue,
  snapshot,
  onClose,
  attentionSignal = 0,
  runtimeUnavailable = false,
  capturePoster,
  device = 'desktop',
}: RegionEditDialogProps): React.JSX.Element {
  const { t, dir } = useWebsiteT();
  const editor = useRegionEditor({ manifest, document, region, queue, snapshot });
  const { close: flushOnClose, hasLocalUnsaved, cancelLocalWork } = editor;
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  // UI/UX P2-1: after a failed save, closing asks (Retry / Leave without saving / Stay).
  const failedClose = useFailedSaveCloseGuard({
    queue,
    snapshot,
    close: () => {
      flushOnClose();
      closeRef.current();
    },
    closeWithoutSaving: () => closeRef.current(),
  });
  const guard = useUnsavedEditGuard({
    isDirty: hasLocalUnsaved,
    onClose: failedClose.requestClose,
  });
  const attentionSeen = useRef(attentionSignal);
  // UI/UX P2-2: the merchant tried to finish while something here is unfinished.
  const [finishRequested, setFinishRequested] = useState(false);
  const requestClose = (): void => {
    if (hasLocalUnsaved) setFinishRequested(true);
    guard.requestClose();
  };
  const requestCloseRef = useRef(requestClose);
  requestCloseRef.current = requestClose;
  // What only this dialog holds (an unfinished link, an unapplied crop, a refused
  // value): one source for the footer status and the leave question.
  const pending = localPendingIn(editor);
  // FWT-p4-dialog-chip-local-invalid: the queue has saved everything it was given, but
  // this dialog still holds a value it may not send. Never claim "All changes saved" then.
  const localOnly =
    hasLocalUnsaved &&
    !editor.isUploading &&
    snapshot.status !== 'conflict' &&
    draftSaveIndicator(snapshot) === 'saved';
  useEffect(() => {
    if (attentionSeen.current === attentionSignal) return;
    attentionSeen.current = attentionSignal;
    if (hasLocalUnsaved) requestCloseRef.current();
  }, [attentionSignal, hasLocalUnsaved]);

  const interfaceLocale = document.interfaceLocale;
  const supabaseUrl = env.VITE_SUPABASE_URL;
  const wide = region.kind === 'media';
  const title = regionEditLabel(t, region);
  const isCampaign =
    manifest.layoutFamily === 'editorial-fashion' &&
    region.scope === 'home' &&
    /^campaign[1-3]$/.test(region.blockId);
  const conflict = snapshot.status === 'conflict';
  const doneRef = useRef<HTMLButtonElement>(null);
  // Fix round 2 (F1): the conflict banner, with the button just pressed, goes
  // away once the merchant's version is on its way; keep focus in the dialog.
  const wasConflict = useRef(conflict);
  useEffect(() => {
    const ended = wasConflict.current && !conflict;
    wasConflict.current = conflict;
    // `document` is the draft prop here; the page is `window.document`. The
    // focus trap parks a lost focus on the dialog itself; either way it is lost.
    const active = window.document.activeElement;
    const lost =
      active === null ||
      active === window.document.body ||
      active.getAttribute('role') === 'dialog';
    if (ended && lost) {
      doneRef.current?.focus();
    }
  }, [conflict]);
  const needsRuntimeRecovery =
    runtimeUnavailable || snapshot.lastErrorCode === 'template_unavailable';

  return (
    <>
      <DialogPrimitive.Root
        open
        onOpenChange={(next) => {
          if (!next) requestClose();
        }}
      >
        <DialogPrimitive.Portal>
          <DialogPrimitive.Overlay
            data-testid="region-dialog-scrim"
            className={cn(
              'fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/50 backdrop-blur-[2px]',
              // P6 (PD12): a media dialog is full width on a phone.
              wide ? 'p-0 sm:p-4' : 'p-4',
            )}
          >
            <DialogPrimitive.Content
              data-testid="region-edit-dialog"
              dir={dir}
              // P6: Escape with an unapplied crop cancels the crop and keeps the
              // dialog open; the next Escape reaches the close guard. (Radix
              // listens on the document in the capture phase, so the editor
              // cannot stop the key itself.)
              onEscapeKeyDown={(event) => {
                if (cancelLocalWork()) event.preventDefault();
              }}
              className={cn(
                'border-border bg-card shadow-admin-modal flex w-full flex-col overflow-hidden border outline-none',
                wide
                  ? 'max-h-dvh sm:max-h-[calc(100dvh-2rem)] sm:max-w-3xl sm:rounded-2xl'
                  : 'max-h-[calc(100dvh-2rem)] max-w-md rounded-2xl',
              )}
            >
              <form
                className="min-h-0 flex-1 overflow-y-auto"
                onSubmit={(event) => {
                  // Enter in a field means "I'm done", never a separate save.
                  event.preventDefault();
                  requestClose();
                }}
              >
                <div className="flex items-start justify-between gap-4 p-6 pb-3">
                  <div className="space-y-1 text-start">
                    <DialogPrimitive.Title className="text-foreground text-lg font-semibold leading-tight">
                      {title}
                    </DialogPrimitive.Title>
                    <DialogPrimitive.Description className="text-muted-foreground text-sm">
                      {region.kind === 'media'
                        ? t('admin.websiteTemplates.media.help.imageOrVideo')
                        : region.kind === 'newsletter'
                          ? t('admin.websiteTemplates.newsletter.help')
                          : t('admin.websiteTemplates.dialog.textHelp')}
                    </DialogPrimitive.Description>
                  </div>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="size-11 shrink-0"
                    aria-label={t('admin.websiteTemplates.common.close')}
                    onClick={requestClose}
                  >
                    <X aria-hidden="true" className="size-4" />
                  </Button>
                </div>

                <fieldset className="min-w-0 px-6 py-3">
                  {isCampaign ? (
                    <p
                      className="border-border bg-admin-surface-muted mb-4 rounded-lg border p-3 text-start text-sm"
                      data-testid="fashion-campaign-binding-hint"
                    >
                      {t('admin.websiteTemplates.fashion.campaignPositionHint')}
                    </p>
                  ) : null}
                  {region.kind === 'media' ? (
                    <RegionMediaForm
                      editor={editor}
                      storeId={storeId}
                      interfaceLocale={interfaceLocale}
                      capturePoster={capturePoster}
                      manifest={manifest}
                      client={client}
                      templateId={templateId}
                      supabaseUrl={supabaseUrl}
                      device={device}
                      finishRequested={finishRequested}
                    />
                  ) : region.kind === 'reference' ? (
                    <RegionReferenceForm editor={editor} storeId={storeId} />
                  ) : region.kind === 'newsletter' ? (
                    <RegionNewsletterForm editor={editor} />
                  ) : (
                    <RegionTextForm
                      editor={editor}
                      storeId={storeId}
                      interfaceLocale={interfaceLocale}
                      client={client}
                      templateId={templateId}
                      supabaseUrl={supabaseUrl}
                      finishRequested={finishRequested}
                    />
                  )}
                </fieldset>

                {/* UI/UX P2-1: status and Done stay in view while the form scrolls. */}
                <div
                  className="border-border bg-card sticky bottom-0 z-10 space-y-3 border-t p-6 pt-3"
                  data-testid="region-dialog-footer"
                >
                  {needsRuntimeRecovery ? (
                    <WebsiteRuntimeNotice
                      storeId={storeId}
                      isDirty={hasLocalUnsaved}
                      // The question covers local-only work; queued edits are sent first.
                      guardReload={(action) =>
                        guard.guard(() => {
                          void queue.flush().finally(action);
                        })
                      }
                    />
                  ) : null}
                  {conflict ? (
                    <div
                      role="alert"
                      data-testid="region-conflict"
                      className="border-border bg-admin-surface-muted space-y-2 rounded-lg border p-3 text-start text-sm"
                    >
                      <p className="text-foreground font-medium">
                        {t('admin.websiteTemplates.editor.save.conflict')}
                      </p>
                      {/* F2: what keeping this window's version does. */}
                      <p className="text-muted-foreground">
                        {t('admin.websiteTemplates.editor.save.conflictHelp')}
                      </p>
                      <Button
                        type="button"
                        variant="outline"
                        className="min-h-11"
                        data-testid="region-conflict-reload"
                        onClick={() => void queue.reload()}
                      >
                        {t('admin.websiteTemplates.editor.save.reload')}
                      </Button>
                    </div>
                  ) : null}
                  {/* Status first: on a phone it sits above Done, matching the tab order. */}
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                    {/* The conflict banner above owns Reload; the chip is hidden then. */}
                    <DraftSaveStatusChip
                      snapshot={snapshot}
                      variant="dialog"
                      hidden={conflict || localOnly}
                      onRetry={() => void queue.retry()}
                    />
                    {localOnly ? (
                      <p
                        role="status"
                        className="text-muted-foreground text-start text-sm"
                        data-testid="region-local-status"
                      >
                        {t(localStatusKey(pending))}
                      </p>
                    ) : null}
                    <Button
                      ref={doneRef}
                      type="button"
                      className="min-h-11 sm:ms-auto"
                      data-testid="region-done"
                      onClick={requestClose}
                    >
                      {t('admin.websiteTemplates.dialog.done')}
                    </Button>
                  </div>
                </div>
              </form>
            </DialogPrimitive.Content>
          </DialogPrimitive.Overlay>
        </DialogPrimitive.Portal>
      </DialogPrimitive.Root>
      <EditorLeaveDialog guard={failedClose.leaveGuard} />
      <UnsavedChangesDialog
        open={guard.confirmOpen}
        onKeepEditing={guard.keepEditing}
        onDiscard={guard.discard}
        copy={leaveCopyOf(pending)}
      />
    </>
  );
}

/** What this dialog holds that the queue does not have, most specific first. */
type LocalPending =
  | { readonly kind: 'destination'; readonly missing: UnfinishedDestination }
  | { readonly kind: 'crop' }
  | { readonly kind: 'value' };

/**
 * UI/UX P2-2 (P5) and epic #228 P6: an unfinished link choice, else an
 * unapplied crop, else a value the schema refused. Null when the dialog holds
 * nothing of its own.
 */
function localPendingIn(editor: RegionEditor): LocalPending | null {
  for (const slot of editor.slots) {
    const state = editor.values[slot.id];
    if (editor.fieldIssues[slot.id] === undefined || state === undefined) continue;
    const missing = unfinishedDestinationOf(slot, state);
    if (missing !== null) return { kind: 'destination', missing };
  }
  if (editor.hasLocalWork) return { kind: 'crop' };
  return editor.hasLocalUnsaved ? { kind: 'value' } : null;
}

/** The footer line while the dialog holds local work. */
function localStatusKey(pending: LocalPending | null): string {
  return pending?.kind === 'destination' || pending?.kind === 'crop'
    ? 'admin.destination.finishToSave'
    : 'admin.websiteTemplates.editor.save.notSaved';
}

/** The leave question names what is missing; a refused value keeps the generic copy. */
function leaveCopyOf(
  pending: LocalPending | null,
):
  | { readonly titleKey: string; readonly bodyKey: string; readonly discardKey: string }
  | undefined {
  if (pending?.kind === 'destination') {
    return {
      titleKey: 'admin.destination.incompleteTitle',
      bodyKey: `admin.destination.incomplete.${pending.missing}`,
      discardKey: 'admin.destination.leaveUnchanged',
    };
  }
  if (pending?.kind === 'crop') {
    return {
      titleKey: 'admin.websiteTemplates.media.framing.unappliedTitle',
      bodyKey: 'admin.websiteTemplates.media.framing.unappliedBody',
      discardKey: 'admin.websiteTemplates.media.framing.leaveUnapplied',
    };
  }
  return undefined;
}
